import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { isAdministratorRole } from '../../lib/roles';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdminPanel?: boolean;
  requireInfazAccess?: boolean;
  requireQrAccess?: boolean;
  requireClientTrackingAccess?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAdminPanel = false,
  requireInfazAccess = false,
  requireQrAccess = false,
  requireClientTrackingAccess = false,
}) => {
  const { loading, isAuthenticated, profile } = useAuth();
  const location = useLocation();

  const isAdministrator = isAdministratorRole(profile?.role_code);

  const canAccessAdminPanel =
    isAdministrator || !!profile?.can_access_admin_panel;

  const canUseInfazCalculator =
    isAdministrator || !!profile?.can_use_infaz_calculator;

  const canUseQrCreator =
    isAdministrator || !!profile?.can_use_qr_creator;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#5A2FCF]"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={{
          redirectTo: location.pathname + location.search + location.hash,
          from: {
            pathname: location.pathname + location.search + location.hash,
          },
        }}
      />
    );
  }

  // Profile is still being fetched asynchronously after login — wait for it
  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#5A2FCF]"></div>
      </div>
    );
  }

  if (!profile.is_active) {
    return <Navigate to="/" replace />;
  }

  if (requireAdminPanel && !canAccessAdminPanel) {
    return <Navigate to="/" replace />;
  }

  if (requireInfazAccess && !canUseInfazCalculator) {
    return <Navigate to="/" replace />;
  }

  if (requireQrAccess && !canUseQrCreator) {
    return <Navigate to="/" replace />;
  }

  if (requireClientTrackingAccess) {
    if (!profile?.can_use_client_tracking) {
      return <Navigate to="/panel" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
